import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DonateButton from '../components/DonateButton';
import SectionDivider from '../components/SectionDivider';
import ProductHero from '../components/products/ProductHero';
import EditionCards from '../components/products/EditionCards';
import EditionDetail from '../components/products/EditionDetail';
import FoundersCollection from '../components/products/FoundersCollection';
import EditionComparison from '../components/products/EditionComparison';
import DeckImpact from '../components/products/DeckImpact';
import InterestForm, { type InterestPreset } from '../components/products/InterestForm';
import { StatusChip, scrollToSection } from '../components/products/shared';
import { usePageMeta } from '../hooks/usePageMeta';
import { OFFERED, FOUNDERS_THEME, type EditionId } from '../products/editions';

/**
 * The Pawsitive Deck (route: /products/pawsitive-deck). A prototype collection shown to gauge interest: no
 * checkout, no stock, no Product structured data, since there is no offer anyone can accept.
 */
const PawsitiveDeckPage: React.FC = () => {
  usePageMeta(
    'The Pawsitive Deck | Pawsitive Future Foundation',
    'Explore The Pawsitive Deck, a custom playing card collection supporting accessible spay and neuter care. Four editions, one mission.',
    '/products/pawsitive-deck'
  );
  const [preset, setPreset] = useState<InterestPreset | null>(null);
  // Nothing is on sale: Buy soon takes the visitor to the vote with that edition picked.
  const buySoon = (choice: EditionId): void => {
    setPreset({ choice, at: Date.now() });
    scrollToSection('interest');
  };
  const standard = OFFERED.filter((e) => e.theme !== FOUNDERS_THEME);
  const founders = OFFERED.find((e) => e.theme === FOUNDERS_THEME);

  return (
    <div>
      <h1 className="sr-only">The Pawsitive Deck</h1>
      <ProductHero />

      <section aria-labelledby="intro-heading" className="pt-16">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <StatusChip className="border-tertiary/40 text-tertiary">Prototype Collection — Gauging Interest</StatusChip>
          <h2 id="intro-heading" className="mt-6 text-3xl sm:text-4xl font-bold text-gray-800">Stack the Deck in Their Favor</h2>
          <div className="mt-6 space-y-4 text-lg text-gray-700">
            <p>The Pawsitive Deck started as a simple idea: turn an everyday game into an opportunity to help animals.</p>
            <p>Our prototype features four custom animal-themed suits, illustrated face cards, and unique pet-inspired artwork.</p>
            <p>
              We&apos;re exploring four different editions, each offering something special while supporting the same mission:
              making spay and neuter care more accessible to families and pets in need.
            </p>
            <p className="font-medium text-gray-800">
              100% of profits from every deck will go into the Pawsitive Future Foundation&apos;s veterinary care voucher pool.
            </p>
          </div>
        </div>
      </section>

      <EditionCards onBuySoon={buySoon} />
      <SectionDivider />

      <section aria-labelledby="details-heading" className="py-8">
        <div className="container max-w-6xl mx-auto px-6">
          <h2 id="details-heading" className="text-3xl font-bold mb-12 text-center text-gray-800">Edition Details</h2>
          <div className="space-y-20">
            {standard.map((edition, i) => (
              <EditionDetail key={edition.id} edition={edition} onBuySoon={buySoon} flip={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      {founders && (
        <section aria-label="Founder's Edition" className={`${FOUNDERS_THEME.surface} mt-20 py-20`}>
          <div className="container max-w-6xl mx-auto px-6 space-y-24">
            <EditionDetail edition={founders} onBuySoon={buySoon} onDark />
            <FoundersCollection />
          </div>
        </section>
      )}

      <EditionComparison />
      <SectionDivider />
      <DeckImpact />
      <InterestForm preset={preset} />

      <section aria-labelledby="mission-cta-heading" className="py-20">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 id="mission-cta-heading" className="text-3xl sm:text-4xl font-bold text-gray-800">
            A Better Future, One Deck at a Time
          </h2>
          <div className="mt-6 space-y-4 text-lg text-gray-700">
            <p>Every game is an opportunity to make a difference.</p>
            <p>
              By supporting The Pawsitive Deck, you&apos;re helping Pawsitive Future Foundation make spay and neuter care more
              accessible, reduce unwanted litters, and support pets and the families who love them.
            </p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <DonateButton label="Support Our Mission" className="btn-primary px-8 py-3 rounded-full font-medium" />
            <Link
              to="/voucher-program"
              className="px-8 py-3 rounded-full font-medium border-2 border-primary text-primary hover:bg-primary/10 transition"
            >
              Learn About Our Voucher Program
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PawsitiveDeckPage;
